"use client";

import { useState } from "react";
import {
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { IdiomCard } from "@/components/tools/word-knowledge/idiom-card";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  IDIOM_THEMES,
  type Idiom,
  type IdiomTheme,
  pickIdiom,
  type RegisterFilter,
  searchIdioms,
} from "@/lib/tool-kit/word-knowledge/idioms";

/** English strings. A translated content file overrides these through `ui`. */
export const IDIOM_FINDER_STRINGS = {
  searchLabel: "Search idioms",
  searchPlaceholder: "money, kill two birds, very easy",
  searchHint:
    "Type a word from the idiom, a word from its meaning, or the idea you want to express.",
  themeLabel: "Theme",
  allThemes: "All themes",
  themeNames: {
    time: "Time",
    money: "Money",
    work: "Work and teamwork",
    success: "Success and failure",
    feelings: "Feelings",
    talk: "Talking and communication",
    effort: "Effort and difficulty",
    risk: "Risk and luck",
    people: "People and relationships",
    truth: "Truth and secrets",
    thinking: "Thinking and learning",
    nature: "Nature, animals and food",
  } as Record<IdiomTheme, string>,
  registerLabel: "Style",
  registers: {
    all: "All idioms",
    neutral: "Fine in most writing",
    informal: "Informal or spoken",
  } as Record<RegisterFilter, string>,
  randomButton: "Show a random idiom",
  randomHeading: "An idiom to try",
  clear: "Clear",
  resultsHeading: "Idioms",
  resultCount: "one:{count} idiom|other:{count} idioms",
  showMore: "Show {count} more",
  noResults:
    "No idioms match. Try a shorter search, a different word from the meaning, or all themes.",
  meaningLabel: "Meaning:",
  exampleLabel: "Example:",
  alsoLabel: "Also:",
  informalBadge: "informal",
  copy: "Copy",
  copied: "Copied!",
  copyWord: "Copy “{word}”",
  styleNote:
    "Idioms are figurative, so most belong in speech and informal writing. In formal academic writing, say the literal meaning instead.",
  sourceNote:
    "Idioms, meanings and example sentences are original to CitationEasy.",
} as const;

const PAGE_SIZE = 24;
const MAX_INPUT = 60;
const REGISTERS: RegisterFilter[] = ["all", "neutral", "informal"];

type ThemeFilter = "all" | IdiomTheme;

function Results({
  idioms,
  strings,
  onTheme,
}: {
  idioms: readonly Idiom[];
  strings: typeof IDIOM_FINDER_STRINGS;
  onTheme: (theme: IdiomTheme) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [shown, setShown] = useState(PAGE_SIZE);
  if (idioms.length === 0) {
    return <Notice>{strings.noResults}</Notice>;
  }
  const remaining = idioms.length - shown;
  return (
    <div className="space-y-4">
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <p className={HINT}>
          {tPlural(strings.resultCount, locale, idioms.length, {
            count: fmt(idioms.length),
          })}
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {idioms.slice(0, shown).map((idiom) => (
          <IdiomCard
            idiom={idiom}
            key={idiom.id}
            onTheme={onTheme}
            strings={strings}
          />
        ))}
      </div>
      {remaining > 0 ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setShown((count) => count + PAGE_SIZE * 4)}
          type="button"
        >
          {t(strings.showMore, {
            count: fmt(Math.min(remaining, PAGE_SIZE * 4)),
          })}
        </button>
      ) : null}
    </div>
  );
}

export default function IdiomFinderWidget() {
  const strings = useToolUi(IDIOM_FINDER_STRINGS);
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState<ThemeFilter>("all");
  const [register, setRegister] = useState<RegisterFilter>("all");
  const [random, setRandom] = useState<Idiom | null>(null);
  const idioms = searchIdioms({ query, theme, register });

  function showRandom() {
    setRandom(pickIdiom(idioms, Math.random()));
  }

  function clear() {
    setQuery("");
    setTheme("all");
    setRegister("all");
    setRandom(null);
  }

  return (
    <>
      <TextField
        hint={strings.searchHint}
        id="idiom-search"
        label={strings.searchLabel}
        onChange={(value) => setQuery(value.slice(0, MAX_INPUT))}
        placeholder={strings.searchPlaceholder}
        type="search"
        value={query}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="idiom-theme"
          label={strings.themeLabel}
          onChange={setTheme}
          options={[
            { value: "all" as ThemeFilter, label: strings.allThemes },
            ...IDIOM_THEMES.map((value) => ({
              value: value as ThemeFilter,
              label: strings.themeNames[value],
            })),
          ]}
          value={theme}
        />
        <SelectField
          id="idiom-register"
          label={strings.registerLabel}
          onChange={setRegister}
          options={REGISTERS.map((value) => ({
            value,
            label: strings.registers[value],
          }))}
          value={register}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={showRandom} type="button">
          {strings.randomButton}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clear} type="button">
          {strings.clear}
        </button>
      </div>
      {random ? (
        <div className={`${RESULT_CARD} space-y-3`}>
          <h2 className={WIDGET_H2}>{strings.randomHeading}</h2>
          <IdiomCard idiom={random} onTheme={setTheme} strings={strings} />
        </div>
      ) : null}
      <Results
        idioms={idioms}
        key={`${query}|${theme}|${register}`}
        onTheme={setTheme}
        strings={strings}
      />
      <p className={HINT}>{strings.styleNote}</p>
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
